// File: frontend/src/views/public/SMSTerms.js

import React from 'react';
import { Link } from 'react-router-dom';
import { MessageSquare, Mail } from 'lucide-react';
import PageContainer from '../../components/layout/PageContainer';
import styles from './LegalPage.module.css';
import SEO from '../../components/utility/SEO';
import { SEO_CONFIG } from '../../config/seo';

const SMSTerms = () => {
  const seo = SEO_CONFIG.smsTerms;

  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      <SEO
        title={seo.title}
        description={seo.description}
        canonical={seo.canonical}
        image={seo.image}
      />
      <PageContainer>
        <div className={styles.page}>

          {/* Header */}
          <div className={styles.header}>
            <h1 className={styles.title}>SMS Terms of Service</h1>
            <p className={styles.effectiveDate}>Effective Date: February 10, 2026</p>
            <p className={styles.intro}>
              These SMS Terms of Service govern the one-time passcode (OTP) text messaging
              program operated by Allegory Technology Inc. ("Allegory," "we," "us," or "our").
              By creating an account, you consent to receive SMS messages as described below.
            </p>
          </div>

          {/* Table of Contents */}
          <nav className={styles.toc}>
            <h2 className={styles.tocTitle}>Table of Contents</h2>
            <ol className={styles.tocList}>
              <li><button onClick={() => scrollToSection('section-1')}>Program Description</button></li>
              <li><button onClick={() => scrollToSection('section-2')}>How to Opt In</button></li>
              <li><button onClick={() => scrollToSection('section-3')}>Message Frequency</button></li>
              <li><button onClick={() => scrollToSection('section-4')}>Message and Data Rates</button></li>
              <li><button onClick={() => scrollToSection('section-5')}>How to Opt Out</button></li>
              <li><button onClick={() => scrollToSection('section-6')}>How to Get Help</button></li>
              <li><button onClick={() => scrollToSection('section-7')}>Carrier Disclaimer</button></li>
              <li><button onClick={() => scrollToSection('section-8')}>Data Privacy</button></li>
              <li><button onClick={() => scrollToSection('section-9')}>Contact Us</button></li>
            </ol>
          </nav>

          {/* Content */}
          <div className={styles.content}>

            {/* Section 1 */}
            <section id="section-1" className={`${styles.section} ${styles.highlightSection}`}>
              <div className={styles.highlightBadge}>
                <MessageSquare size={24} strokeWidth={2} />
              </div>
              <h2 className={styles.sectionTitle}>1. Program Description</h2>
              <p className={styles.highlightText}>
                <strong>Program Name:</strong> Allegory Insurance OTP Verification
              </p>
              <p>
                Allegory Insurance sends one-time passcode (OTP) SMS messages solely for
                account verification and passwordless authentication purposes. When you sign
                up or sign in to your Allegory account using your phone number, we send a
                7-digit verification code to your mobile device to confirm your identity.
              </p>
              <p>
                We do not send promotional, marketing, or advertising messages via SMS.
                All SMS messages sent by Allegory are transactional and are triggered
                exclusively by your own sign-up or sign-in actions.
              </p>
            </section>

            {/* Section 2 */}
            <section id="section-2" className={styles.section}>
            <h2 className={styles.sectionTitle}>2. How to Opt In</h2>
            <p>
                You opt in to receive SMS verification messages through one of two surfaces:
            </p>

            <h3 className={styles.subsectionTitle}>2.1 Account Creation (Sign-Up)</h3>
            <p>
                During account creation, you must check a mandatory consent checkbox that reads:
            </p>
            <div className={styles.contactInfo}>
                <p>
                <em>
                    "By creating an account, I agree to the Terms of Service, Privacy Policy,
                    and SMS Terms. I consent to receive one-time passcode (OTP) SMS messages
                    from Allegory Insurance for account verification. One message per sign up
                    attempt. Message and data rates may apply. Text STOP to opt out or HELP
                    for support, or email support@allegoryinsurance.com."
                </em>
                </p>
            </div>
            <p>
                This checkbox is unchecked by default and must be actively selected by you.
                Your account cannot be created without completing this step. By checking this
                box, you provide express written consent to receive SMS messages from
                Allegory Insurance.
            </p>

            <h3 className={styles.subsectionTitle}>2.2 Sign-In via Phone Number</h3>
            <p>
                If you choose to sign in using your phone number rather than your email address,
                a consent notice is displayed before you submit your number. The notice reads:
            </p>
            <div className={styles.contactInfo}>
                <p>
                <em>
                    "By continuing, you consent to receive a one-time verification code via SMS
                    from Allegory Insurance. Message and data rates may apply. One message per
                    sign in attempt. Reply STOP to opt out or HELP for support, or email
                    support@allegoryinsurance.com."
                </em>
                </p>
            </div>
            <p>
                By entering your phone number and tapping Continue, you provide express consent
                to receive a one-time SMS verification code for that sign-in attempt.
            </p>

            <p>
                In both cases, consent is explicit, action-initiated by you, and occurs before
                any SMS message is sent.
            </p>
            </section>

            {/* Section 3 */}
            <section id="section-3" className={styles.section}>
              <h2 className={styles.sectionTitle}>3. Message Frequency</h2>
              <p>
                Message frequency varies based on your account activity. You will receive
                one SMS message per sign-up or sign-in attempt. You will only receive a
                message when you actively initiate an authentication request on our platform.
              </p>
              <p>
                Each verification code expires within <strong>2 minutes</strong> of being
                sent. If your code expires, you may request a new one, which will generate
                an additional SMS message.
              </p>
            </section>

            {/* Section 4 */}
            <section id="section-4" className={styles.section}>
              <h2 className={styles.sectionTitle}>4. Message and Data Rates</h2>
              <p>
                <strong>Message and data rates may apply.</strong> The cost of receiving
                SMS messages depends on your mobile carrier plan. Allegory does not charge
                you for SMS messages, but your wireless provider may charge standard
                messaging and data rates.
              </p>
              <p>
                If you have questions about your text plan or data plan, contact your
                wireless provider directly.
              </p>
            </section>

            {/* Section 5 */}
            <section id="section-5" className={styles.section}>
            <h2 className={styles.sectionTitle}>5. How to Opt Out</h2>
            <p>
                You can opt out of Allegory SMS messages at any time by replying{' '}
                <strong>STOP</strong> to any SMS message you receive from us. After sending
                STOP, you will receive one final confirmation message acknowledging your
                opt-out request. No further SMS messages will be sent to your number.
            </p>
            <div className={styles.highlight}>
                <p>
                <strong>Important:</strong> Because Allegory uses passwordless SMS-based
                authentication, opting out of SMS means you will no longer be able to
                sign in or recover your account using your phone number. If you wish to
                permanently stop receiving SMS messages, you must delete your account.
                You can do this from your account settings or by contacting us at{' '}
                <a href="mailto:support@allegoryinsurance.com">
                    support@allegoryinsurance.com
                </a>.
                </p>
            </div>
            <p>
                If you opt out and later wish to re-enroll, you may do so by creating a
                new account or by replying <strong>START</strong> to a previous message
                from Allegory and initiating a new sign-in request.
            </p>

            <h3 className={styles.subsectionTitle}>5.1 STOP Confirmation Message</h3>
            <p>
                When you reply STOP, you will receive the following confirmation message
                from Allegory Insurance:
            </p>
            <div className={styles.contactInfo}>
                <p>
                <em>
                    "You are unsubscribed from Allegory Insurance OTP. To opt back in,
                    reply START or request a new verification code. Help:
                    support@allegoryinsurance.com"
                </em>
                </p>
            </div>
            <p>
                No further SMS messages will be sent to your number after this
                confirmation, unless you re-enroll.
            </p>

            <h3 className={styles.subsectionTitle}>5.2 OTP Re-Enrollment After Opt-Out</h3>
            <p>
                Because Allegory uses SMS-based passwordless authentication, a subsequent
                request for a verification code by you (by entering your phone number on
                the sign-in screen) is considered a new opt-in action. You do not need to
                reply <strong>START</strong> if you initiate a new authentication request
                yourself.
            </p>
            </section>

            {/* Section 6 */}
            <section id="section-6" className={styles.section}>
              <h2 className={styles.sectionTitle}>6. How to Get Help</h2>
              <p>
                If you are experiencing issues with our SMS verification program, you can
                get help in the following ways:
              </p>
              <ul>
                <li>
                  Reply <strong>HELP</strong> to any SMS message from Allegory Insurance
                </li>
                <li>
                  Email us at{' '}
                  <a href="mailto:support@allegoryinsurance.com">
                    support@allegoryinsurance.com
                  </a>
                </li>
                <li>
                  Visit our <Link to="/contact">Contact page</Link>
                </li>
              </ul>
              <p>
                We will respond to all support inquiries within 30 days.
              </p>
            </section>

            {/* Section 7 */}
            <section id="section-7" className={styles.section}>
              <h2 className={styles.sectionTitle}>7. Carrier Disclaimer</h2>
              <p>
                Carriers are not liable for delayed or undelivered messages. SMS delivery
                depends on your mobile carrier and network conditions. If you do not receive
                a verification code, please check your signal, ensure your phone number is
                correct, and request a new code.
              </p>
              <p>
                Allegory SMS messages are delivered via AWS Simple Notification Service
                (SNS). Supported carriers include all major US and Canadian wireless
                providers.
              </p>
            </section>

            {/* Section 8 */}
            <section id="section-8" className={styles.section}>
              <h2 className={styles.sectionTitle}>8. Data Privacy</h2>
              <p>
                Your phone number and SMS opt-in consent are used solely for account
                authentication. SMS opt-in data and consent will not be shared with any
                third parties. This expressly excludes SMS opt-in information from any
                data sharing practices described elsewhere in our{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>.
              </p>
              <p>
                For full details on how we collect, use, and protect your personal
                information, please review our{' '}
                <Link to="/privacy-policy">Privacy Policy</Link>, specifically{' '}
                <Link to="/privacy-policy#section-9">Section 9: SMS Verification and Consent</Link>.
              </p>
            </section>

            {/* Section 9 */}
            <section id="section-9" className={styles.section}>
              <h2 className={styles.sectionTitle}>9. Contact Us</h2>
              <p>
                If you have questions or concerns about this SMS Terms of Service, please
                contact us:
              </p>
              <div className={styles.contactInfo}>
                <p><strong>Allegory Technology Inc.</strong></p>
                <p>Toronto Exchange Tower</p>
                <p>130 King Street West, Suite 1900</p>
                <p>Toronto, ON, M5X 1E3, Canada</p>
                <p>
                  Email:{' '}
                  <a href="mailto:support@allegoryinsurance.com">
                    support@allegoryinsurance.com
                  </a>
                </p>
              </div>
            </section>

          </div>

          {/* Footer CTA */}
          <div className={styles.footerCTA}>
            <p>Questions about our SMS program?</p>
            <Link to="/contact" className={styles.ctaButton}>
              <Mail size={20} strokeWidth={2} />
              Contact Us
            </Link>
          </div>

        </div>
      </PageContainer>
    </>
  );
};

export default SMSTerms;